import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { 
  Ship, Mail, FileText, FlaskConical, BarChart3, Settings, 
  LogOut, Plus, Upload, Menu, X 
} from "lucide-react";

// Tab Components
import VesselList from "../components/vessel/VesselList";
import CampaignList from "../components/campaign/CampaignList";
import TemplateList from "../components/template/TemplateList";
import ABTestList from "../components/abtest/ABTestList";
import AnalyticsDashboard from "../components/analytics/AnalyticsDashboard";
import SettingsModal from "../components/settings/SettingsModal";

// Modal Components
import AddVesselModal from "../components/vessel/AddVesselModal";
import ImportVesselsModal from "../components/vessel/ImportVesselsModal";
import CreateCampaignModal from "../components/campaign/CreateCampaignModal";
import CreateTemplateModal from "../components/template/CreateTemplateModal";
import CreateABTestModal from "../components/abtest/CreateABTestModal";

const tabs = [
  { id: "vessels", label: "Vessels", icon: Ship },
  { id: "campaigns", label: "Campaigns", icon: Mail },
  { id: "templates", label: "Templates", icon: FileText },
  { id: "ab-tests", label: "A/B Tests", icon: FlaskConical },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("vessels");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Stats
  const [vesselStats, setVesselStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [campaignCount, setCampaignCount] = useState(0);
  const [templateCount, setTemplateCount] = useState(0);
  const [abTestCount, setABTestCount] = useState(0);
  
  // Modals
  const [showAddVessel, setShowAddVessel] = useState(false);
  const [showImportVessels, setShowImportVessels] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showCreateABTest, setShowCreateABTest] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Single refresh counter to avoid multiple state updates
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const [vesselsRes, campaignsRes, templatesRes, abTestsRes] = await Promise.all([
        api.get("/vessels/stats"),
        api.get("/campaigns"),
        api.get("/templates"),
        api.get("/ab-tests")
      ]);
      
      setVesselStats(vesselsRes.data);
      setCampaignCount(campaignsRes.data.length);
      setTemplateCount(templatesRes.data.length);
      setABTestCount(abTestsRes.data.length);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, refreshKey]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  const getTabCount = (tabId) => {
    switch (tabId) {
      case "vessels": return vesselStats.total;
      case "campaigns": return campaignCount;
      case "templates": return templateCount;
      case "ab-tests": return abTestCount;
      default: return null;
    }
  };

  const triggerRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case "vessels":
        return <VesselList key={`vessels-${refreshKey}`} onRefresh={triggerRefresh} />;
      case "campaigns":
        return <CampaignList key={`campaigns-${refreshKey}`} onRefresh={triggerRefresh} />;
      case "templates":
        return <TemplateList key={`templates-${refreshKey}`} onRefresh={triggerRefresh} />;
      case "ab-tests":
        return <ABTestList key={`ab-tests-${refreshKey}`} onRefresh={triggerRefresh} />;
      case "analytics":
        return <AnalyticsDashboard />;
      default:
        return null;
    }
  };

  const renderActionButtons = () => {
    switch (activeTab) {
      case "vessels":
        return (
          <>
            <Button
              onClick={() => setShowImportVessels(true)}
              variant="outline"
              className="gap-2"
              data-testid="import-vessels-btn"
            >
              <Upload className="h-4 w-4" />
              Import
            </Button>
            <Button
              onClick={() => setShowAddVessel(true)}
              className="btn-primary gap-2"
              data-testid="add-vessel-btn"
            >
              <Plus className="h-4 w-4" />
              Add Vessel
            </Button>
          </>
        );
      case "campaigns":
        return (
          <Button
            onClick={() => setShowCreateCampaign(true)}
            className="btn-primary gap-2"
            data-testid="create-campaign-btn"
          >
            <Plus className="h-4 w-4" />
            Create Campaign
          </Button>
        );
      case "templates":
        return (
          <Button
            onClick={() => setShowCreateTemplate(true)}
            className="btn-primary gap-2"
            data-testid="create-template-btn"
          >
            <Plus className="h-4 w-4" />
            Create Template
          </Button>
        );
      case "ab-tests":
        return (
          <Button
            onClick={() => setShowCreateABTest(true)}
            className="btn-primary gap-2"
            data-testid="create-abtest-btn"
          >
            <Plus className="h-4 w-4" />
            Create A/B Test
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 hover:bg-white/5 rounded"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div className="flex items-center gap-2">
              <Ship className="h-6 w-6 text-primary" />
              <span className="text-lg font-bold hidden sm:inline" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Vessel Email
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSettings(true)}
              data-testid="settings-btn"
            >
              <Settings className="h-5 w-5" />
            </Button>
            
            <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-border">
              <div className="text-right">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="h-8 w-8 rounded-full"
                />
              )}
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Mobile */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-30 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <nav className="relative w-64 h-full bg-card border-r border-border p-4">
              <div className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const count = getTabCount(tab.id);
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded transition-colors ${
                        activeTab === tab.id
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                      }`}
                      data-testid={`nav-${tab.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <span>{tab.label}</span>
                      </div>
                      {count !== null && (
                        <span className="text-xs bg-secondary px-2 py-0.5 rounded-full">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>
        )}

        {/* Sidebar - Desktop */}
        <nav className="hidden lg:block w-64 min-h-[calc(100vh-4rem)] bg-card/50 border-r border-border p-4">
          <div className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded transition-colors ${
                    activeTab === tab.id
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                  }`}
                  data-testid={`nav-${tab.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                  </div>
                  {count !== null && (
                    <span className="text-xs bg-secondary px-2 py-0.5 rounded-full">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {tabs.find(t => t.id === activeTab)?.label}
              </h1>
              <p className="text-muted-foreground mt-1">
                {activeTab === "vessels" && "Manage your vessel contacts"}
                {activeTab === "campaigns" && "Create and track email campaigns"}
                {activeTab === "templates" && "Build reusable email templates"}
                {activeTab === "ab-tests" && "Optimize with A/B testing"}
                {activeTab === "analytics" && "View performance metrics"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {renderActionButtons()}
            </div>
          </div>

          {/* Tab Content */}
          <div className="animate-fadeIn">
            {renderTabContent()}
          </div>
        </main>
      </div>

      {/* Modals */}
      {showAddVessel && (
        <AddVesselModal
          onClose={() => setShowAddVessel(false)}
          onSuccess={() => {
            setShowAddVessel(false);
            triggerRefresh();
          }}
        />
      )}

      {showImportVessels && (
        <ImportVesselsModal
          onClose={() => setShowImportVessels(false)}
          onSuccess={() => {
            setShowImportVessels(false);
            triggerRefresh();
          }}
        />
      )}

      {showCreateCampaign && (
        <CreateCampaignModal
          onClose={() => setShowCreateCampaign(false)}
          onSuccess={() => {
            setShowCreateCampaign(false);
            triggerRefresh();
          }}
        />
      )}

      {showCreateTemplate && (
        <CreateTemplateModal
          onClose={() => setShowCreateTemplate(false)}
          onSuccess={() => {
            setShowCreateTemplate(false);
            triggerRefresh();
          }}
        />
      )}

      {showCreateABTest && (
        <CreateABTestModal
          onClose={() => setShowCreateABTest(false)}
          onSuccess={() => {
            setShowCreateABTest(false);
            triggerRefresh();
          }}
        />
      )}

      {showSettings && (
        <SettingsModal onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}
